"use client";

// Pre-prod audit R25 — wrap the React tree so any unhandled render error
// flows to Sentry (when configured) instead of dropping into the void.
// Used in app/layout.tsx; per-route boundaries can wrap subtrees if a
// specific section needs a different fallback UI.

import React from "react";
import * as Sentry from "@sentry/nextjs";

interface Props {
    children: React.ReactNode;
    fallback?: React.ReactNode;
}

interface State {
    hasError: boolean;
}

export default class ErrorBoundary extends React.Component<Props, State> {
    state: State = { hasError: false };

    static getDerivedStateFromError(): State {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo): void {
        try {
            Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
        } catch {
            // Sentry not initialised (no DSN configured) — fall through.
        }
    }

    render() {
        if (this.state.hasError) {
            return (
                this.props.fallback ?? (
                    <div style={{ padding: 32, textAlign: "center" }}>
                        <h1 style={{ fontSize: 20, marginBottom: 8 }}>Something went wrong.</h1>
                        <p style={{ color: "#6b7280", marginBottom: 16 }}>
                            We&rsquo;ve been notified. Try reloading the page.
                        </p>
                        <button
                            type="button"
                            onClick={() => window.location.reload()}
                            style={{
                                padding: "8px 16px",
                                borderRadius: 6,
                                background: "#3D5BDB",
                                color: "white",
                                border: "none",
                                cursor: "pointer",
                            }}
                        >
                            Reload
                        </button>
                    </div>
                )
            );
        }
        return this.props.children;
    }
}
